import React, { useState, useEffect, useCallback } from 'react';
import { Store as StoreIcon, ShoppingBag, CalendarClock, ArrowRight, Check } from 'lucide-react';
import { navigate } from '../App';
import { ImageCropModal } from '../components/ImageCropModal';
import { checkSlugAvailability } from '../hooks/useStoreApi';
import { getVocab, OFFERING_GOODS, OFFERING_APPOINTMENTS } from '../utils/storeVocabulary';

// The two personalities a store can take, shown as the first choice at creation.
const OFFERING_OPTIONS = [
    {
        type: OFFERING_GOODS,
        icon: ShoppingBag,
        title: 'מוצרים',
        tagline: 'דברים שהלקוחות קונים ולוקחים',
        examples: 'אוכל · מוצרים · יצירה · קמעונאות',
        accent: 'warning',
    },
    {
        type: OFFERING_APPOINTMENTS,
        icon: CalendarClock,
        title: 'תורים',
        tagline: 'מפגשים ושירותים שקובעים איתך',
        examples: 'טיפולים · ייעוץ · שיעורים · קליניקה',
        accent: 'info',
    },
];

// Per-accent Tailwind classes (kept static so the JIT compiler keeps them).
const ACCENT_STYLES = {
    warning: {
        ringSel: 'border-warning-500 bg-warning-50 ring-2 ring-warning-200',
        ringIdle: 'border-line hover:border-warning-300 hover:bg-warning-50/40',
        iconSel: 'bg-warning-500 text-white',
        iconIdle: 'bg-warning-50 text-warning-600',
        check: 'bg-warning-500',
    },
    info: {
        ringSel: 'border-info-500 bg-info-50 ring-2 ring-info-200',
        ringIdle: 'border-line hover:border-info-300 hover:bg-info-50/40',
        iconSel: 'bg-info-500 text-white',
        iconIdle: 'bg-info-50 text-info-600',
        check: 'bg-info-500',
    },
};

const API_URL = process.env.REACT_APP_STORE_API_URL || 'https://v3ugnvc3qk.execute-api.us-west-2.amazonaws.com/prod';

export const CreateStoreModal = ({ onClose, userId, businessId, onStoreCreated }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [cropModal, setCropModal] = useState(null); // { type: 'cover'|'profile', image: string }
    const [slugAvailability, setSlugAvailability] = useState({ checking: false, available: null, message: '' });
    // Two-step flow: pick what you're creating, then fill in the details.
    const [step, setStep] = useState('type'); // 'type' | 'details'

    // Overlay creation loading feedback
    const [showCreationOverlay, setShowCreationOverlay] = useState(false); // controls visibility
    const [overlayPhase, setOverlayPhase] = useState('idle'); // 'idle' | 'creating' | 'done'

    const [formData, setFormData] = useState({
        offeringType: OFFERING_GOODS,
        name: '',
        slug: '',
        about: '',
        coverImage: null,
        profileImage: null,
        coverCrop: null,
        profileCrop: null
    });

    // Resolved vocabulary for the chosen offering type (drives all copy below).
    const t = getVocab(formData.offeringType);

    const handleSelectOffering = (offeringType) => {
        setFormData(prev => ({ ...prev, offeringType }));
        setStep('details');
    };

    // Debounced slug availability check
    useEffect(() => {
        if (!formData.slug || formData.slug.length < 3) {
            setSlugAvailability({ checking: false, available: null, message: '' });
            return;
        }

        // Validate slug format
        if (!/^[a-z0-9-]+$/.test(formData.slug)) {
            setSlugAvailability({
                checking: false,
                available: false,
                message: 'כתובת לא תקינה - השתמש רק באותיות אנגליות קטנות, מספרים ומקפים'
            });
            return;
        }

        setSlugAvailability({ checking: true, available: null, message: 'בודק זמינות...' });

        const timeoutId = setTimeout(async () => {
            try {
                const result = await checkSlugAvailability(formData.slug);
                if (result.available) {
                    setSlugAvailability({
                        checking: false,
                        available: true,
                        message: '✓ הכתובת זמינה'
                    });
                } else {
                    setSlugAvailability({
                        checking: false,
                        available: false,
                        message: '✗ הכתובת תפוסה - נסה כתובת אחרת'
                    });
                }
            } catch (err) {
                console.error('Error checking slug availability:', err);
                setSlugAvailability({
                    checking: false,
                    available: null,
                    message: 'שגיאה בבדיקת זמינות'
                });
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [formData.slug]);

    const handleNameChange = (e) => {
        const name = e.target.value;
        const slug = name
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .slice(0, 50);

        setFormData(prev => ({ ...prev, name, slug }));
    };

    const handleSlugChange = (e) => {
        const slug = e.target.value.toLowerCase();
        setFormData(prev => ({ ...prev, slug }));
    };

    const handleImageChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('גודל התמונה גדול מדי. מקסימום 5MB');
                return;
            }

            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError('יש להעלות קובץ תמונה בלבד');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                // Open crop modal after image loads
                setCropModal({
                    type,
                    image: reader.result
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCropComplete = (crop) => {
        if (!cropModal) return;

        const { type, image } = cropModal;

        // Store the image and crop parameters
        setFormData(prev => ({
            ...prev,
            [type]: image,
            [`${type}Crop`]: crop
        }));

        setCropModal(null);
    };

    const uploadImageToS3 = async (base64Data, uploadType) => {
        if (!base64Data) return null;

        try {
            // Extract file type from base64 data
            const matches = base64Data.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
            if (!matches || matches.length !== 3) {
                throw new Error('Invalid image format');
            }

            const fileType = matches[1];

            // Get presigned POST data
            const urlResponse = await fetch(`${API_URL}/temp-upload-url`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    file_type: fileType,
                    upload_type: uploadType
                })
            });

            if (!urlResponse.ok) {
                throw new Error('Failed to get upload URL');
            }

            const { upload_url, upload_fields, s3_key } = await urlResponse.json();

            // Convert base64 to blob
            const blob = await fetch(base64Data).then(r => r.blob());

            // Upload to S3 using POST with FormData (avoids CORS preflight)
            const formData = new FormData();

            // Add all fields from presigned post first
            Object.keys(upload_fields).forEach(key => {
                formData.append(key, upload_fields[key]);
            });

            // Add the file last
            formData.append('file', blob);

            const uploadResponse = await fetch(upload_url, {
                method: 'POST',
                body: formData
            });

            if (!uploadResponse.ok) {
                throw new Error('Failed to upload image');
            }

            return s3_key;
        } catch (err) {
            console.error(`Error uploading ${uploadType} image:`, err);
            throw err;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setShowCreationOverlay(true);
        setOverlayPhase('creating');

        try {
            // Upload images to S3 first
            let coverImageKey = null;
            let profileImageKey = null;

            if (formData.coverImage) {
                coverImageKey = await uploadImageToS3(formData.coverImage, 'cover');
            }

            if (formData.profileImage) {
                profileImageKey = await uploadImageToS3(formData.profileImage, 'profile');
            }

            // Create store with S3 keys and crop parameters
            const response = await fetch(`${API_URL}/store`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    owner_id: userId,
                    name: formData.name,
                    store_slug: formData.slug,
                    about: formData.about,
                    offering_type: formData.offeringType,
                    cover_image_key: coverImageKey,
                    profile_image_key: profileImageKey,
                    cover_crop: formData.coverCrop,
                    profile_crop: formData.profileCrop,
                    business_id: businessId  // Include business_id if provided
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'שגיאה ביצירת החנות');
            }

            const result = await response.json();

            // Call the callback to update parent component state
            if (onStoreCreated) {
                onStoreCreated({
                    has_store: true,
                    store_id: result.store_id,
                    store_slug: result.store_slug,
                    offering_type: result.offering_type || formData.offeringType
                });
            }
            // Begin graceful fade-out before navigation
            setOverlayPhase('done');
            // Delay navigation until fade completes (500ms matches CSS duration)
            setTimeout(() => {
                navigate('store-admin', `slug=${result.store_slug}`);
                onClose();
            }, 550);
        } catch (err) {
            console.error('Error creating store:', err);
            setError(err.message);
            // If error occurs, hide overlay (fade quickly) so user can retry
            setOverlayPhase('done');
            setTimeout(() => {
                setShowCreationOverlay(false);
                setOverlayPhase('idle');
            }, 400);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center z-[10000] p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-elevated max-w-2xl w-full mx-4 h-[90vh] flex flex-col animate-scale-in"
                onClick={(e) => e.stopPropagation()}
                dir="rtl"
            >
                <style>{`
                  input[type="text"], textarea { color: #1f1f1f !important; }
                  .input-field:focus { box-shadow: 0 0 0 3px rgba(226, 183, 123, 0.1) !important; }
                `}</style>
                <form onSubmit={handleSubmit} className="flex flex-col h-full">
                    {/* Header */}
                    <div className="px-8 py-7 border-b border-line-subtle flex justify-between items-start">
                        <div className="flex items-start gap-3">
                            {step === 'details' && (
                                <button
                                    type="button"
                                    onClick={() => setStep('type')}
                                    className="mt-1 text-ink-subtle hover:text-ink-strong w-9 h-9 flex items-center justify-center rounded-lg hover:bg-surface-muted transition-all duration-200"
                                    disabled={loading}
                                    aria-label="חזרה"
                                >
                                    {/* RTL: "back" points right */}
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                            )}
                            <div>
                                <h2 className="text-3xl font-bold text-ink-strong mb-1">
                                    {step === 'type' ? 'מה תרצה ליצור?' : t.createStoreTitle}
                                </h2>
                                <p className="text-ink-subtle text-sm">
                                    {step === 'type'
                                        ? 'הבחירה הזו מעצבת את כל החוויה — אפשר תמיד לשנות פרטים בהמשך'
                                        : `הוסף את הפרטים הבסיסיים של ${t.storeNounDefinite} שלך`}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-ink-subtle hover:text-ink-muted text-2xl w-10 h-10 flex items-center justify-center rounded-lg hover:bg-surface-muted transition-all duration-200"
                            disabled={loading}
                            aria-label="סגור"
                        >
                            ×
                        </button>
                    </div>

                    {/* Scrollable content */}
                    <div className="flex-1 overflow-y-auto px-8 py-8 space-y-7">
                        {error && (
                            <div className="bg-danger-50 border border-danger-100 text-danger-700 px-5 py-4 rounded-xl text-sm font-medium flex items-start gap-3">
                                <div className="text-lg mt-0.5">⚠️</div>
                                <div>{error}</div>
                            </div>
                        )}

                        {/* Step 1: choose what you're creating */}
                        {step === 'type' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
                                {OFFERING_OPTIONS.map((opt) => {
                                    const Icon = opt.icon;
                                    const selected = formData.offeringType === opt.type;
                                    const s = ACCENT_STYLES[opt.accent];
                                    return (
                                        <button
                                            key={opt.type}
                                            type="button"
                                            onClick={() => handleSelectOffering(opt.type)}
                                            className={`group relative text-right rounded-2xl border-2 p-6 transition-all duration-200 active:scale-[0.98] ${selected ? s.ringSel : s.ringIdle}`}
                                        >
                                            {selected && (
                                                <span className={`absolute top-4 left-4 w-6 h-6 rounded-full flex items-center justify-center ${s.check}`}>
                                                    <Check className="w-4 h-4 text-white" strokeWidth={3} />
                                                </span>
                                            )}
                                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-all duration-200 ${selected ? s.iconSel : s.iconIdle}`}>
                                                <Icon className="w-7 h-7" strokeWidth={1.75} />
                                            </div>
                                            <h3 className="text-xl font-bold text-ink-strong mb-1.5">{opt.title}</h3>
                                            <p className="text-ink-muted text-sm leading-relaxed mb-3">{opt.tagline}</p>
                                            <p className="text-ink-subtle text-xs">{opt.examples}</p>
                                            <div className="mt-5 flex items-center gap-1.5 text-sm font-semibold text-ink-subtle group-hover:text-ink-strong transition-colors">
                                                <span>בחירה</span>
                                                <ArrowRight className="w-4 h-4 rotate-180" />
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Step 2: the details (wording adapts to the chosen type) */}
                        {step === 'details' && (
                        <>
                        {/* Store Name */}
                        <div className="space-y-3">
                            <div className="flex items-baseline justify-between">
                                <label className="block text-ink-strong font-semibold text-sm">
                                    {`שם ${t.storeNounDefinite}`}
                                </label>
                                <span className="text-ink-subtle text-xs">{formData.name.length}/50</span>
                            </div>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={handleNameChange}
                                className="input-field w-full px-5 py-3.5 bg-surface-muted border border-line rounded-xl focus:outline-none focus:border-warning-500 focus:bg-white transition-all duration-200 text-ink-strong placeholder-ink-subtle"
                                placeholder="לדוגמה: לחמניית יעל"
                                required
                                disabled={loading}
                                maxLength="50"
                            />
                            <p className="text-ink-subtle text-xs">{`שם ייחודי שיזהה את ${t.storeNounDefinite} שלך`}</p>
                        </div>

                        {/* Store URL */}
                        <div className="space-y-3">
                            <label className="block text-ink-strong font-semibold text-sm">
                                כתובת URL
                            </label>
                            <div className="flex items-center gap-2 bg-surface-muted rounded-xl border border-line focus-within:border-warning-500 focus-within:bg-white transition-all duration-200 p-0.5">
                                <span className="text-ink-subtle text-sm font-medium px-4 whitespace-nowrap">mekomit.com/store/</span>
                                <input
                                    type="text"
                                    value={formData.slug}
                                    onChange={handleSlugChange}
                                    className={`input-field flex-1 px-2 py-3 bg-transparent focus:outline-none text-ink-strong placeholder-ink-subtle ${slugAvailability.available === false
                                        ? 'text-danger-600'
                                        : slugAvailability.available === true
                                            ? 'text-success-600'
                                            : ''
                                        }`}
                                    pattern="[a-z0-9-]+"
                                    required
                                    disabled={loading}
                                    placeholder="shop-name"
                                />
                            </div>
                            {slugAvailability.message && (
                                <p className={`text-xs font-medium flex items-center gap-1.5 ${slugAvailability.checking
                                    ? 'text-ink-subtle'
                                    : slugAvailability.available
                                        ? 'text-success-600'
                                        : 'text-danger-600'
                                    }`}>
                                    {slugAvailability.checking && <span>⏳</span>}
                                    {slugAvailability.available && <span>✓</span>}
                                    {slugAvailability.available === false && <span>✕</span>}
                                    {slugAvailability.message}
                                </p>
                            )}
                            {!slugAvailability.message && (
                                <p className="text-ink-subtle text-xs">אותיות אנגליות קטנות, מספרים ומקפים בלבד</p>
                            )}
                        </div>

                        {/* About Store */}
                        <div className="space-y-3">
                            <div className="flex items-baseline justify-between">
                                <label className="block text-ink-strong font-semibold text-sm">
                                    {`אודות ${t.storeNounDefinite}`}
                                </label>
                                <span className="text-ink-subtle text-xs">{formData.about.length}/500</span>
                            </div>
                            <textarea
                                value={formData.about}
                                onChange={(e) => setFormData(prev => ({ ...prev, about: e.target.value.slice(0, 500) }))}
                                className="input-field w-full px-5 py-3.5 bg-surface-muted border border-line rounded-xl focus:outline-none focus:border-warning-500 focus:bg-white transition-all duration-200 resize-none text-ink-strong placeholder-ink-subtle"
                                rows="4"
                                placeholder={`ספר/י על ${t.storeNounDefinite} שלך, ה${t.items} שלך, והחזון שלך...`}
                                disabled={loading}
                                maxLength="500"
                            />
                            <p className="text-ink-subtle text-xs">זה יעזור ללקוחות להבין את המיוחד שלך</p>
                        </div>

                        {/* Image Upload Section */}
                        <div className="pt-4">
                            <h3 className="text-sm font-semibold text-ink-strong mb-5">{`תמונות ${t.storeNounDefinite}`}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                                {/* Cover Image */}
                                <div className="space-y-3">
                                    <label className="block text-ink font-medium text-sm">
                                        תמונת כיסוי
                                    </label>
                                    <div className="relative group">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleImageChange(e, 'coverImage')}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                            disabled={loading}
                                        />
                                        <div className="px-5 py-6 bg-gradient-to-br from-warning-50 to-amber-25 border-2 border-dashed border-warning-100 rounded-xl flex flex-col items-center justify-center gap-2 group-hover:border-warning-100 group-hover:bg-amber-75 transition-all duration-200">
                                            <div className="text-2xl">📷</div>
                                            <p className="text-sm font-medium text-ink">בחר תמונה</p>
                                            <p className="text-xs text-ink-subtle">עד 5MB</p>
                                        </div>
                                    </div>
                                    {formData.coverImage && (
                                        <div className="relative group/image">
                                            <img
                                                src={formData.coverImage}
                                                alt="תצוגה מקדימה"
                                                className="w-full h-32 rounded-xl object-cover border border-line"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setCropModal({ type: 'coverImage', image: formData.coverImage })}
                                                className="absolute inset-0 w-full h-full rounded-xl bg-black/0 group-hover/image:bg-black/40 flex items-center justify-center opacity-0 group-hover/image:opacity-100 transition-all duration-200"
                                            >
                                                <span className="text-white font-semibold text-sm">✂️ עריכה</span>
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Profile Image */}
                                <div className="space-y-3">
                                    <label className="block text-ink font-medium text-sm">
                                        תמונת פרופיל
                                    </label>
                                    <div className="relative group">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleImageChange(e, 'profileImage')}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                            disabled={loading}
                                        />
                                        <div className="px-5 py-6 bg-gradient-to-br from-info-50 to-blue-25 border-2 border-dashed border-info-100 rounded-xl flex flex-col items-center justify-center gap-2 group-hover:border-info-100 group-hover:bg-blue-75 transition-all duration-200">
                                            <div className="text-2xl">👤</div>
                                            <p className="text-sm font-medium text-ink">בחר תמונה</p>
                                            <p className="text-xs text-ink-subtle">עד 5MB</p>
                                        </div>
                                    </div>
                                    {formData.profileImage && (
                                        <div className="relative group/image flex justify-center">
                                            <div className="relative">
                                                <img
                                                    src={formData.profileImage}
                                                    alt="תצוגה מקדימה"
                                                    className="w-32 h-32 rounded-full object-cover border-2 border-line"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setCropModal({ type: 'profileImage', image: formData.profileImage })}
                                                    className="absolute inset-0 w-full h-full rounded-full bg-black/0 group-hover/image:bg-black/40 flex items-center justify-center opacity-0 group-hover/image:opacity-100 transition-all duration-200"
                                                >
                                                    <span className="text-white font-semibold text-sm">✂️</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        </>
                        )}
                    </div>

                    {/* Footer (only on the details step — the chooser cards are the action) */}
                    {step === 'details' && (
                    <div className="px-8 py-6 border-t border-line-subtle bg-surface-muted rounded-b-2xl flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setStep('type')}
                            className="px-7 py-3 rounded-lg bg-white text-ink font-semibold transition-all duration-200 hover:bg-surface-muted border border-line active:scale-95"
                            disabled={loading}
                        >
                            חזרה
                        </button>
                        <button
                            type="submit"
                            className="px-7 py-3 rounded-lg bg-gradient-to-r from-warning-500 to-warning-600 text-white font-semibold transition-all duration-200 hover:from-warning-600 hover:to-warning-700 disabled:from-line disabled:to-line-strong disabled:cursor-not-allowed shadow-sm hover:shadow-md active:scale-95"
                            disabled={loading || slugAvailability.checking || slugAvailability.available === false}
                        >
                            {loading ? (
                                <span className="flex items-center gap-2.5">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    {`יוצר את ${t.storeNounDefinite}...`}
                                </span>
                            ) : (
                                `צור ${t.storeNoun}`
                            )}
                        </button>
                    </div>
                    )}
                </form>
            </div>

            {cropModal && (
                <ImageCropModal
                    image={cropModal.image}
                    aspectRatio={cropModal.type === 'coverImage' ? 16 / 9 : 1}
                    onClose={() => setCropModal(null)}
                    onCrop={handleCropComplete}
                />
            )}

            {showCreationOverlay && (
                <div
                    className={`fixed inset-0 z-[999] flex flex-col items-center justify-center backdrop-blur-sm transition-opacity duration-500 ${overlayPhase === 'done' ? 'opacity-0' : 'opacity-100'} bg-white/90`}
                    aria-live="polite"
                >
                    <style>{`
                      @keyframes rotateStoreRing { to { transform: rotate(360deg); } }
                      @keyframes dashStoreRing {
                        0% { stroke-dasharray: 4 340; stroke-dashoffset: 0; }
                        45% { stroke-dasharray: 220 340; stroke-dashoffset: -80; }
                        70% { stroke-dasharray: 120 340; stroke-dashoffset: -200; }
                        100% { stroke-dasharray: 4 340; stroke-dashoffset: -340; }
                      }
                      @keyframes subtlePulseStore { 0%,100% { transform: scale(1); } 50% { transform: scale(1.045); } }
                    `}</style>
                    <div className="relative w-44 h-44 mb-8">
                        {/* Animated SVG progress ring */}
                        <svg
                            viewBox="0 0 120 120"
                            className="absolute inset-0 w-full h-full animate-[rotateStoreRing_7s_linear_infinite]"
                            role="img"
                            aria-label="Progress"
                        >
                            <defs>
                                <linearGradient id="storeRingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.15" />
                                    <stop offset="55%" stopColor="#2563eb" stopOpacity="0.55" />
                                    <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0.85" />
                                </linearGradient>
                                <linearGradient id="storeRingTrail" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#bfdbfe" stopOpacity="0.25" />
                                    <stop offset="100%" stopColor="#93c5fd" stopOpacity="0.15" />
                                </linearGradient>
                            </defs>
                            {/* Trail */}
                            <circle
                                cx="60"
                                cy="60"
                                r="54"
                                fill="none"
                                stroke="url(#storeRingTrail)"
                                strokeWidth="6"
                            />
                            {/* Animated arc */}
                            <circle
                                cx="60"
                                cy="60"
                                r="54"
                                fill="none"
                                stroke="url(#storeRingGradient)"
                                strokeWidth="6"
                                strokeLinecap="round"
                                style={{
                                    strokeDasharray: '4 340',
                                    strokeDashoffset: 0,
                                    animation: 'dashStoreRing 2.9s ease-in-out infinite'
                                }}
                            />
                        </svg>
                        {/* Icon container */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl p-5 border border-info-100 animate-[subtlePulseStore_3.4s_ease-in-out_infinite]">
                                <StoreIcon className="w-16 h-16 text-info-600" strokeWidth={1.75} />
                            </div>
                        </div>
                    </div>
                    <div className="text-center">
                        <p className="text-lg font-medium text-ink mb-2">{`יוצר את ${t.storeNounDefinite}, זה ייקח כמה רגעים`}</p>
                    </div>
                </div>
            )}
        </div>
    );
};
